import React, { useState } from 'react';
import EnhancedAnalysisLoading from '../components/EnhancedAnalysisLoading';

const LoadingDemo: React.FC = () => {
  const [currentStage, setCurrentStage] = useState<'uploading' | 'analyzing' | 'processing' | 'complete'>('uploading');
  const [progress, setProgress] = useState(0);
  const [apiProvider, setApiProvider] = useState('Gemini');

  const startDemo = () => {
    setCurrentStage('uploading');
    setProgress(0);
    
    // Simulate uploading
    setTimeout(() => {
      setProgress(30);
      setCurrentStage('analyzing');
    }, 2000);
    
    // Simulate analyzing
    setTimeout(() => {
      setProgress(80);
      setCurrentStage('processing');
    }, 8000);
    
    // Simulate completing
    setTimeout(() => {
      setProgress(100);
      setCurrentStage('complete');
    }, 10000);
  };

  const resetDemo = () => {
    setCurrentStage('uploading');
    setProgress(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Enhanced Loading Animation Demo
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Experience the new AI analysis loading animation with advanced visual effects
          </p>
          
          <div className="flex justify-center space-x-4 mb-6">
            <button
              onClick={startDemo}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              Start Demo
            </button>
            <button
              onClick={resetDemo}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              Reset
            </button>
          </div>

          <div className="flex justify-center space-x-4 mb-8">
            <select
              value={apiProvider}
              onChange={(e) => setApiProvider(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="Gemini">Gemini AI</option>
              <option value="GPT-4 Vision">GPT-4 Vision</option>
              <option value="Claude Vision">Claude Vision</option>
              <option value="Grok Vision">Grok Vision</option>
            </select>
          </div>
        </div>

        <EnhancedAnalysisLoading
          currentStage={currentStage}
          apiProvider={apiProvider}
          progress={progress}
          onComplete={() => console.log('Demo completed!')}
        />

        <div className="mt-12 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Features Showcase
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🎨 Particle System</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Dynamic particle animation that changes based on the current analysis stage
              </p>
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🧠 Neural Network Visualization</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Animated neural nodes during AI analysis phase
              </p>
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">⚡ Enhanced Progress Bar</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Gradient progress bar with shimmer effects and animations
              </p>
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">💡 AI Insights</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Rotating tips explaining what the AI is doing during analysis
              </p>
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🎯 Stage Indicators</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Visual progress through upload, analysis, processing, and completion stages
              </p>
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🌈 Dynamic Colors</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Color scheme changes based on current stage with smooth transitions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingDemo;
